import { CROSSPOST_POSTS_PAGE_SIZE } from '@pulsify/shared/dto'
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { crosspostingApi } from '~/entities/crossposting/api/crossposting-api'
import * as m from '~/paraglide/messages'
import { getErrorMessageFromError } from '~/shared/lib/api-errors'
import { QueryKey, queryKey } from '~/shared/lib/query-keys'

const TERMINAL_STATUSES = new Set(['posted', 'error', 'deleted'])

export function useCrosspostPosts(businessId: string, listingId: string) {
  return useInfiniteQuery({
    queryKey: queryKey(QueryKey.CROSSPOST_POSTS, businessId, listingId),
    queryFn: ({ pageParam }) =>
      crosspostingApi.listPosts(businessId, listingId, CROSSPOST_POSTS_PAGE_SIZE, pageParam),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) =>
      lastPage.hasMore ? allPages.length * CROSSPOST_POSTS_PAGE_SIZE : undefined,
    enabled: !!businessId && !!listingId,
    // The webhook keeps the DB authoritative; poll only while something is still
    // non-terminal so a freshly published post flips to its final state on its own.
    refetchInterval: (query) => {
      const pending = query.state.data?.pages.some((page) =>
        page.posts.some((post) => !TERMINAL_STATUSES.has(post.status)),
      )
      return pending ? 10_000 : false
    },
  })
}

/**
 * Re-reads pending posts from bundle.social before refetching. The webhook is the
 * primary path; this is the manual fallback for a post whose webhook never landed.
 */
export function useRefreshCrosspostPosts(businessId: string, listingId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () =>
      crosspostingApi.refreshPosts(businessId, listingId, CROSSPOST_POSTS_PAGE_SIZE),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKey(QueryKey.CROSSPOST_POSTS, businessId, listingId),
      })
    },
    onError: (error) => {
      toast.error(getErrorMessageFromError(error))
    },
  })
}

export function useDeleteCrosspostPost(businessId: string, listingId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (postId: string) => crosspostingApi.deletePost(businessId, listingId, postId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKey(QueryKey.CROSSPOST_POSTS, businessId, listingId),
      })
      toast.success(m.crosspostingDeleted())
    },
    onError: (error) => {
      toast.error(getErrorMessageFromError(error))
    },
  })
}
